import { useModal } from '@ebay/nice-modal-react'
import { useNavigate, useParams } from '@tanstack/router'
import type { ChangeEvent, MouseEvent } from 'react'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import useDimensions from '../../hooks/useDimensions'
import useGeoCheck from '../../hooks/useGeoCheck'
import { useBoundStore } from '../../store/store'
import type { ApiResponse } from '../../types/api'
import type { Game } from '../../types/game'
import { api } from '../../utils/api-client'
import classNames from '../../utils/classNames'
import { debounce } from '../../utils/common'
import { Spinner, ToastMessage } from '../common'
import LoginModal from '../common/log-in-modal'
import Icon from '../icon'

const SearchBar = () => {
  const navigate = useNavigate()
  const { isDesktop } = useDimensions()
  const { category } = useParams({ from: '/casino/$category' })
  const pending_bind = useBoundStore(state => state.user.pending_bind)
  const setIsPendingDepositVisible = useBoundStore(
    state => state.common.setIsPendingDepositVisible,
  )
  const username = useBoundStore(state => state.user.username)
  const { geoCheck } = useGeoCheck()
  const logInModal = useModal(LoginModal)
  const toastMessage = useModal(ToastMessage)
  const { t } = useTranslation('casino')
  const [isFocus, setIsFocus] = useState(false)
  const [searchKeyword, setSearchKeyword] = useState('')
  const [filteredList, setFilteredList] = useState<Game[]>([])
  const [isLoading, setIsLoading] = useState(false)

  const getHighlightedText = useCallback(
    (text: string) => {
      // Split on highlight term and include term into parts, ignore case
      const parts = text.split(new RegExp(`(${searchKeyword})`, 'gi'))
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap' }}>
          {parts.map((part, i) => (
            <span
              key={i}
              className={classNames(
                part.toLowerCase() === searchKeyword.toLowerCase()
                  ? 'text-[#46aef7]'
                  : 'text-txt',
                'text-xs leading-[14px] xl:text-sm xl:leading-4',
              )}>
              {part}
            </span>
          ))}
        </div>
      )
    },
    [searchKeyword],
  )

  const search = async (e: ChangeEvent<HTMLInputElement>) => {
    setSearchKeyword(e.target.value)
    if (e.target.value.length < 3) {
      setFilteredList([])
      return
    }
    setIsLoading(true)
    try {
      const res = await api.post<Game[]>('/request.php', {
        type: 'search_game',
        key: e.target.value,
      })
      if (Array.isArray(res.data)) {
        setFilteredList(res.data)
      }
    } catch (error) {
      console.error(error)
    }
    setIsLoading(false)
  }

  const onPlayGame = async (e: MouseEvent<HTMLDivElement>, game: Game) => {
    ;(e.target as HTMLDivElement).classList.add('[&>.spinner]:flex')

    // Transfer balance to game
    api.post('/ajax_data.php', {
      type: 'auto_transfer_in',
      ...game.gameInfo,
    })

    const reqBody = {
      type: 'play_game_websocket2',
      id: game.gameInfo.id,
      platform: isDesktop ? 0 : 1,
    }

    const response = await api.post<ApiResponse<string>>(
      '/request.php',
      reqBody,
    )

    const { info, status } = response.data
    if (status === 1) {
      navigate({
        to: '/casino/$category/$game',
        search: {
          gameUrl: encodeURIComponent(info),
          gameId: game.gameInfo.gameId,
          gameCode: game.gameInfo.gameCode,
          isFavorite: '0',
        },
        params: {
          category,
          game: `${game.name.replaceAll(/\W/g, '')}-${game.gameInfo.id}`,
        },
      })
    } else if (typeof info === 'string') {
      toastMessage.show({
        text: info,
        isValid: false,
      })
    }

    ;(e.target as HTMLDivElement).classList.remove('[&>.spinner]:flex')
  }

  return (
    <div
      className={classNames(
        'flx-str-ctr w-full border-[color:var(--secondary-txt-50)] transition-all duration-[0.2s] ease-[ease-in-out]',
        'relative bg-primary text-secondary-txt px-2 py-1 rounded-md border-[1.5px] border-solid',
        'focus-within:text-highlight focus-within:border-highlight xl:hover:text-highlight xl:hover:border-highlight',
      )}>
      <span className="w-[15px] flex mr-1.5 xl:w-[17px]">
        <Icon id="search" width="16" height="16" />
      </span>
      <input
        className="text-txt text-sm w-full xl:text-base [&>svg]:transition-all [&>svg]:duration-[0.2s] [&>svg]:ease-[ease-in-out]"
        onFocus={() => setIsFocus(true)}
        onBlur={() => setTimeout(() => setIsFocus(false), 200)}
        placeholder={t('search')}
        onChange={debounce(search, 300)}
      />
      <div
        className={classNames(
          searchKeyword.length >= 3 && isFocus
            ? 'translate-y-0 opacity-100 pointer-events-auto'
            : '-translate-y-2.5 opacity-0 pointer-events-none',
          'casino-searchbar absolute w-screen grid gap-3 z-10 max-h-[321px] overflow-y-auto transition-all duration-[0.3s] ease-[ease-in-out]',
          'grid-cols-[1fr_1fr] px-3 py-3.5 rounded-xl -left-2 top-[calc(100%+10px)] xl:w-[calc(100%+6px)] xl:left-0 xl:top-[calc(100%+6px)] xl:casino-searchbar-scrollbar',
        )}>
        {isLoading ? (
          <Spinner containerStyle={{ gridColumn: '1 / 3' }} />
        ) : filteredList.length === 0 ? (
          <p className="col-[1/3] text-sm text-[#999] font-normal text-center">
            {t('no-games')}
          </p>
        ) : (
          filteredList.slice(0, 30).map((item, index) => (
            <div
              className="casino-searchbar-item flx-str-ctr w-full min-w-0 cursor-pointer transition-all duration-[0.2s] ease-[ease-in-out] relative rounded-[10px] [&>*]:pointer-events-none"
              key={index}
              onClick={e => {
                if (!username) {
                  return geoCheck(logInModal.show)
                }
                if (+pending_bind === 0 || +pending_bind === 2) {
                  setIsPendingDepositVisible(true)
                  return
                }
                onPlayGame(e, item)
              }}>
              <div className="spinner absolute w-[50px] h-[50px] hidden left-0 top-0 xl:w-[55px] xl:h-[55px]">
                <Spinner spinnerSize={25} />
              </div>
              <img
                className="w-[50px] min-w-[50px] h-[50px] object-cover mr-2.5 rounded-[5px] xl:w-[55px] xl:min-w-[55px] xl:h-[55px]"
                src={item.imgURL}
                alt={item.name}
              />
              {getHighlightedText(item.name)}
            </div>
          ))
        )}
      </div>
    </div>
  )
}

export default SearchBar
